import type { IMetricControl } from '~/modules/page-analytic/types';
import type { EMetricKey } from '~/types/metric';

import { BlockStack, Box, Card, Icon, InlineStack, Text } from '@shopify/polaris';
import { usePageAnalyticFilterStore } from '~/modules/page-analytic/stores';
import { usePageAnalyticDetailContext } from '~/modules/page-analytic';
import { LOCATION_PATH_KEY } from '~/modules/page-analytic/constants';
import { PAMetricInfoSkeleton } from './common/PAMetricInfoSkeleton';
import { GBlockCenter, GDiv, GTooltipCard } from '~/presentation/ui';
import { EReportSource } from '~/modules/report-analytic/types';
import { useNavigateToReport } from '~/modules/report-analytic';
import { ANALYTICS_METRIC_TOOLTIP } from '~/modules/analytics';
import { ChevronRightIcon } from '@shopify/polaris-icons';
import { useSearchParams } from '@remix-run/react';
import { useCallback, useState } from 'react';
import { PALineChart } from './PALineChart';
import { PAPercent } from './PAPercent';

export interface PVAnalyticsData {
  key: EMetricKey;
  title: string;
  control: IMetricControl;
}

export interface PALineChartGroupProps {
  defaultActiveTab?: EMetricKey;
  analyticsData: PVAnalyticsData[];
  isLoading?: boolean;
}

export const PALineChartGroup: React.FC<PALineChartGroupProps> = ({
  defaultActiveTab,
  analyticsData,
  isLoading,
}) => {
  const { hideComparison } = usePageAnalyticDetailContext();
  const analyticMode = usePageAnalyticFilterStore((state) => state.analyticMode);

  const [activeTab, setActiveTab] = useState<EMetricKey | undefined>(defaultActiveTab);
  const [mouseEnter, setMouseEnter] = useState<EMetricKey | undefined>();

  const [searchParams] = useSearchParams();
  const locationPath = searchParams.get(LOCATION_PATH_KEY);

  const { handleNavigateToReport } = useNavigateToReport();

  const handleMouseEnter = useCallback((key: EMetricKey) => {
    setMouseEnter(key);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setMouseEnter(undefined);
  }, []);

  const handleClick = useCallback((key: EMetricKey) => {
    setActiveTab(key);
  }, []);

  const renderMetricCard = (item: PVAnalyticsData) => {
    const { key, title, control } = item;
    const { value, change } = control;
    const tooltip = ANALYTICS_METRIC_TOOLTIP[key];

    return (
      <GDiv
        className="cursor-pointer w-full overflow-hidden"
        onMouseEnter={() => handleMouseEnter(key)}
        onMouseLeave={handleMouseLeave}
        onClick={() => handleClick(key)}
        key={key}
      >
        <Box
          paddingBlock="150"
          paddingInline="300"
          borderRadius="200"
          {...((activeTab === key || mouseEnter === key) && {
            background: 'bg-surface-active',
          })}
        >
          {isLoading ? (
            <PAMetricInfoSkeleton />
          ) : (
            <BlockStack gap="200">
              <GDiv
                className="hover:text-[--p-color-text-link-hover]"
                onClick={(e) => {
                  e?.stopPropagation();
                  handleNavigateToReport(key, {
                    locationPath,
                    mode: analyticMode,
                    from: EReportSource.PAGE,
                  });
                }}
              >
                <InlineStack wrap={false}>
                  <Box maxWidth="100%" overflowX="hidden">
                    <GTooltipCard tooltip={tooltip}>
                      <Text as="span" variant="headingSm" fontWeight="semibold" truncate>
                        {title}
                      </Text>
                    </GTooltipCard>
                  </Box>
                  {mouseEnter === key && (
                    <GBlockCenter width="20px" height="20px">
                      <Icon source={ChevronRightIcon} tone="inherit" />
                    </GBlockCenter>
                  )}
                </InlineStack>
              </GDiv>

              <PAPercent
                value={value}
                change={change}
                hideComparison={hideComparison}
                metricKey={key}
              />
            </BlockStack>
          )}
        </Box>
      </GDiv>
    );
  };

  return (
    <Card>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${analyticsData.length}, 1fr)`,
          gap: '16px',
          marginBottom: '16px',
        }}
      >
        {analyticsData.map((item) => renderMetricCard(item))}
      </div>

      <PALineChart activeTab={activeTab} />
    </Card>
  );
};
